import { z } from "zod";

/**
 * Shared DTO cho Admin Debts Oversight + force-settle/write-off (US-E15.F6-0181 · @UC9227-9230) —
 * liệt kê TOÀN BỘ DebtEntry/Loan xuyên quỹ/nhóm (READ-ONLY) + can thiệp tất toán/xoá nợ (MUTATION).
 * Một nguồn sự thật cho api (S02/S03/S04) và admin web (S05) — tránh drift shape (DRY). Mirror style
 * admin-expenses.ts.
 *
 * BẢO MẬT (invariants — LOAD-BEARING):
 * - REDACTION (F1 MONEY_VISIBLE_ROLES): `amountMinor` NULLABLE — SUPPORT/READ_ONLY nhận null,
 *   SUPER_ADMIN/OPS nhận string. Item KHÔNG khai báo PII thô (email/phone/primaryUser).
 * - B1: `amountMinor` = BigInt minor-units → STRING (regex số nguyên, no float/number JS).
 * - C3: `.strict()` — field lạ bị reject tại biên.
 * - force-settle/write-off: `reason` BẮT BUỘC (mọi can thiệp có dấu vết) + `idempotencyKey` (ledger key).
 * - Cursor opaque string; envelope `{ data, pageInfo: { hasNext, nextCursor } }`.
 */

/** Max page-size (data lớn → 100). Default (20) do backend áp khi vắng (normalizeLimit). */
export const ADMIN_DEBTS_MAX_LIMIT = 100;

/** Số nguyên minor-units dạng string (BigInt-safe, KHÔNG float). Nullable = redacted vai thấp. */
const amountMinorString = z
  .string()
  .regex(/^-?\d+$/, "amountMinor phải là số nguyên minor units (không float)")
  .nullable()
  .optional();

/**
 * `kind`/`status` filter đa-giá-trị (multi-select) — nhận 1 giá trị | CSV | repeated param → string[].
 * KHÔNG hard-code enum (DebtEntry.kind mở dần: on_behalf/expense_split/loan/settle/offset/refund/
 * advance_credit/forgiven; Loan.status: open/partially_repaid/repaid) — nhận chuỗi ≥1, filter server-side.
 */
const stringListSchema = z
  .union([z.string(), z.array(z.string())])
  .transform((v) =>
    (Array.isArray(v) ? v : v.split(",")).map((s) => s.trim()).filter((s) => s.length > 0),
  )
  .pipe(z.array(z.string().min(1).max(64)).min(1))
  .optional();

/**
 * Query list (AC1 filter server-side + cursor):
 *  - `fundId?`/`groupId?`/`debtorMemberId?` (uuid) — lọc theo quỹ/nhóm/con nợ.
 *  - `kind?`/`status?` — 1|CSV|array (vắng ⇒ TOÀN BỘ).
 *  - `q?` — full-text tên member (≤256) server-side.
 *  - `cursor?` opaque, `limit?` ∈ [1, ADMIN_DEBTS_MAX_LIMIT].
 */
export const adminDebtsQuerySchema = z
  .object({
    fundId: z.string().uuid().optional(),
    groupId: z.string().uuid().optional(),
    debtorMemberId: z.string().uuid().optional(),
    kind: stringListSchema,
    status: stringListSchema,
    q: z.string().trim().min(1).max(256).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_DEBTS_MAX_LIMIT).optional(),
  })
  .strict();

export type AdminDebtsQuery = z.infer<typeof adminDebtsQuerySchema>;

/**
 * List item (AC1). `amountMinor` NULLABLE (redact vai thấp). `creditorMemberId` optional (fund-flow
 * = null). `fundName`/`groupName`/`debtorName`/`creditorName` = display-name/redacted (KHÔNG email/
 * phone thô). `.strict()` reject field lạ tại biên.
 */
export const adminDebtItemSchema = z
  .object({
    id: z.string(),
    source: z.enum(["debt_entry", "loan"]),
    fundId: z.string(),
    fundName: z.string().nullable().optional(),
    groupId: z.string().nullable().optional(),
    groupName: z.string().nullable().optional(),
    debtorMemberId: z.string(),
    debtorName: z.string().nullable().optional(),
    creditorMemberId: z.string().nullable().optional(),
    creditorName: z.string().nullable().optional(),
    kind: z.string(),
    amountMinor: amountMinorString,
    currency: z.string(),
    status: z.string().nullable().optional(),
    createdAt: z.string(),
  })
  .strict();

export type AdminDebtItem = z.infer<typeof adminDebtItemSchema>;

/** Envelope khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminDebtsResponseSchema = z.object({
  data: z.array(adminDebtItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminDebtsResponse = z.infer<typeof adminDebtsResponseSchema>;

/**
 * Body can thiệp force-settle (tất toán thủ công 1 nợ ròng): `reason` BẮT BUỘC (≥3 ký tự, có dấu vết
 * ai/vì-sao) + `idempotencyKey` (ledger unique key chống double-post race).
 */
export const forceSettleSchema = z
  .object({
    reason: z.string().trim().min(3, "reason bắt buộc (≥3 ký tự)").max(500),
    idempotencyKey: z.string().min(1).max(128),
  })
  .strict();

export type ForceSettleBody = z.infer<typeof forceSettleSchema>;

/** Body write-off (xoá nợ khó đòi = forgiven, P4.3): reason BẮT BUỘC + idempotencyKey. */
export const writeOffSchema = z
  .object({
    reason: z.string().trim().min(3, "reason bắt buộc (≥3 ký tự)").max(500),
    idempotencyKey: z.string().min(1).max(128),
  })
  .strict();

export type WriteOffBody = z.infer<typeof writeOffSchema>;

/** Response can thiệp (force-settle/write-off) — trả txId ledger vừa post + trạng thái nợ ròng mới. */
export const adminDebtActionResponseSchema = z
  .object({
    ledgerTxId: z.string(),
    debtEntryId: z.string(),
    amountMinor: z.string().regex(/^\d+$/),
    currency: z.string(),
    remainingNetMinor: z.string().regex(/^\d+$/),
  })
  .strict();

export type AdminDebtActionResponse = z.infer<typeof adminDebtActionResponseSchema>;
